import { CLIENT_CPV_MIN_DIGITS, cpvPrefixOf } from '@elsa/contracts';
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Loading,
  Note,
  Page,
  Table,
  Td,
} from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { OPERATOR } from '../config.ts';
import { fmtDate } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, must, rpc } from '../lib/supabase.ts';
import type { OrgCpv } from '../lib/types.ts';
import { useOrgCpv } from './AppLayout.tsx';
import { CompanyForm } from './Billing.tsx';

// Setarile firmei (docs/23), pentru cine are settings.manage: datele firmei, cum apare firma
// in SEAP (ca furnizor) si codurile CPV urmarite. Codurile se aleg o singura data, pana se
// umple pachetul; nu se sterg si nu se schimba din Elsa (le schimbam noi, la cerere). Firma
// vede in Elsa doar achizitiile pe codurile ei.

interface Supplier {
  id: number;
  entity_name: string;
  numeric_fiscal_number: string | null;
  county: string | null;
  city: string | null;
}

function SeapName() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const o = useQuery({
    // Alta cheie decat formularul firmei (CompanyForm), care citeste tot randul.
    queryKey: ['organization', org.id, 'seap'],
    queryFn: async () =>
      (
        (await must(
          app().from('organizations').select('seap_name, seap_supplier_id').eq('id', org.id),
        )) as { seap_name: string | null; seap_supplier_id: number | null }[]
      )[0],
  });
  const found = useQuery({
    queryKey: ['find_suppliers', q],
    enabled: q.trim().length >= 3,
    queryFn: () => rpc<Supplier[]>('find_suppliers', { p_q: q.trim() }),
  });
  const save = useMutation({
    mutationFn: (patch: { seap_name: string | null; seap_supplier_id?: number | null }) =>
      must(app().from('organizations').update(patch).eq('id', org.id)),
    onSuccess: () => {
      setQ('');
      void qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
  if (!o.data) return <Loading />;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = String(new FormData(e.currentTarget).get('seap_name') ?? '').trim();
    save.mutate({ seap_name: v || null });
  };
  return (
    <div className="space-y-3">
      <form className="flex flex-wrap items-end gap-2" onSubmit={submit}>
        <div className="min-w-64 flex-1">
          <Field
            label="Denumirea firmei în SEAP"
            help="Cum apare firma ca furnizor în SEAP, ca să-i recunoaștem contractele câștigate."
          >
            <Input
              key={o.data.seap_name ?? ''}
              name="seap_name"
              defaultValue={o.data.seap_name ?? ''}
              maxLength={300}
            />
          </Field>
        </div>
        <Button type="submit" disabled={save.isPending}>
          Salvează
        </Button>
      </form>
      <p className="text-sm">
        Fișa de furnizor din SEAP:{' '}
        {o.data.seap_supplier_id ? (
          <a className="underline" href={`/app/furnizori/${o.data.seap_supplier_id}`}>
            deschide fișa
          </a>
        ) : (
          <span className="text-gri-700">negăsită încă</span>
        )}
      </p>
      <Field label="Caută firma printre furnizorii din SEAP (nume sau CUI)">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ex. Medical Distribuție"
        />
      </Field>
      {found.data?.length ? (
        <Table head={['Furnizor', 'CUI', 'Localitatea', '']}>
          {found.data.map((s) => (
            <tr key={s.id}>
              <Td>{s.entity_name}</Td>
              <Td>{s.numeric_fiscal_number ?? '—'}</Td>
              <Td>{[s.city, s.county].filter(Boolean).join(', ') || '—'}</Td>
              <Td>
                <Button
                  onClick={() => save.mutate({ seap_name: s.entity_name, seap_supplier_id: s.id })}
                >
                  E firma noastră
                </Button>
              </Td>
            </tr>
          ))}
        </Table>
      ) : q.trim().length >= 3 && found.isSuccess ? (
        <p className="text-xs text-gri-700">Niciun furnizor găsit.</p>
      ) : null}
      <ErrorBox error={save.error ?? found.error} />
    </div>
  );
}

function CpvCodes() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const cpv = useOrgCpv();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const found = useQuery({
    queryKey: ['search_cpv', q],
    enabled: q.trim().length >= 2,
    queryFn: () => rpc<{ code: string; label: string | null }[]>('search_cpv', { p_q: q.trim() }),
  });
  const add = useMutation({
    mutationFn: () => rpc<OrgCpv>('add_org_cpv', { p_org: org.id, p_codes: picked }),
    onSuccess: () => {
      setPicked([]);
      setQ('');
      void qc.invalidateQueries({ queryKey: ['org_cpv'] });
      void qc.invalidateQueries({ queryKey: ['filter_options'] });
    },
  });
  if (!cpv.data) return <Loading />;
  const c = cpv.data;
  const room = c.limit === null ? Number.POSITIVE_INFINITY : c.limit - c.used - picked.length;
  const pick = (code: string) => {
    setHint(null);
    const p = cpvPrefixOf(code);
    if (p.length < CLIENT_CPV_MIN_DIGITS) {
      setHint(`${code} acoperă un domeniu întreg: îl activăm noi, la cerere.`);
      return;
    }
    const clash = [...c.codes.map((x) => x.code), ...picked].find(
      (x) => p.startsWith(x) || x.startsWith(p),
    );
    if (clash) {
      setHint(`${code} se suprapune cu ${clash}, deja ales.`);
      return;
    }
    if (room <= 0) {
      setHint('Pachetul e plin.');
      return;
    }
    setPicked([...picked, p]);
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-gri-800">
        {c.limit === null
          ? `${c.used} coduri, fără limită în pachet.`
          : `${c.used} din ${c.limit} coduri din pachet.`}{' '}
        Elsa arată doar achizițiile pe aceste coduri (inclusiv pe cele care au măcar un produs pe
        ele).
      </p>
      {c.codes.length ? (
        <Table head={['Cod', 'Denumire', 'Ales de', 'Din']}>
          {c.codes.map((x) => (
            <tr key={x.code}>
              <Td className="font-mono">{x.code}</Td>
              <Td>{x.label ?? '—'}</Td>
              <Td>
                <Badge tone={x.source === 'admin' ? 'info' : 'neutral'}>
                  {x.source === 'admin' ? 'echipa Elsa' : 'firma'}
                </Badge>
              </Td>
              <Td>{fmtDate(x.added_at)}</Td>
            </tr>
          ))}
        </Table>
      ) : null}
      {c.can_add ? (
        <div className="space-y-2 border-2 border-cerneala p-3">
          <Field
            label="Adaugă coduri CPV"
            help={`Caută după cod sau denumire. De la ${CLIENT_CPV_MIN_DIGITS} cifre în sus (clasă, categorie); un domeniu întreg îl activăm noi.`}
          >
            <Input
              name="cpv_q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ex. 3314 sau seringi"
            />
          </Field>
          {found.data?.length ? (
            <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
              {found.data.map((x) => (
                <li key={x.code} className="flex items-center justify-between gap-2">
                  <span>
                    <span className="font-mono">{x.code}</span> {x.label}
                  </span>
                  <Button onClick={() => pick(x.code)}>Alege</Button>
                </li>
              ))}
            </ul>
          ) : null}
          {hint ? <Note>{hint}</Note> : null}
          {picked.length ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {picked.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="border-2 border-cerneala px-2 py-0.5 font-mono text-xs font-semibold hover:bg-cerneala/5"
                    onClick={() => setPicked(picked.filter((x) => x !== p))}
                    title="Scoate din listă"
                  >
                    {p} ×
                  </button>
                ))}
              </div>
              <Note>
                Codurile salvate nu se mai pot șterge sau schimba din Elsa. Verifică lista înainte.
              </Note>
              <Button
                tone="primary"
                disabled={add.isPending}
                onClick={() => {
                  if (
                    confirm(`Salvezi ${picked.length} coduri CPV? Nu se mai pot schimba din Elsa.`)
                  )
                    add.mutate();
                }}
              >
                Salvează codurile
              </Button>
            </div>
          ) : null}
          <ErrorBox error={add.error} />
        </div>
      ) : null}
      <p className="text-xs text-gri-700">
        Codurile se aleg o singură dată. Pentru alte coduri, un domeniu întreg sau mai multe coduri
        decât are pachetul, scrie-ne
        {OPERATOR.email.startsWith('[') ? '' : ` la ${OPERATOR.email}`}.
      </p>
    </div>
  );
}

export function SettingsPage() {
  return (
    <Page title="Setări firmă">
      <Card title="Codurile CPV urmărite">
        <CpvCodes />
      </Card>
      <Card title="Firma în SEAP">
        <SeapName />
      </Card>
      <Card title="Datele firmei">
        <CompanyForm />
      </Card>
    </Page>
  );
}
